import { z } from 'zod';

/**
 * Rechte-Level der Nutzer der DXL-Plattform (Rolle dxl_admin, ohne Marktplatz).
 * Das Level bestimmt die Plattform-Rechte – fest je Level, nicht einzeln wählbar.
 * Bestehende DXL-Admins sind Administratoren (Migration 0380).
 */
export const PLATTFORM_RECHTE = [
  /** Marktplätze, deren Details und die Betriebsart ansehen */
  'marktplaetze_lesen',
  /** Marktplätze anlegen, bearbeiten, sperren; Inhaber einladen; in der DXL-Ansicht ändern */
  'marktplaetze_bearbeiten',
  /** Konto eines Marktplatzes öffnen (DXL-Ansicht) */
  'ansicht',
  /** Pakete, Konditionen, Rechnungen und KI-Preise ansehen */
  'abrechnung_lesen',
  /** Pakete & Abrechnung ändern: Rechnungen erstellen, senden, Status setzen */
  'abrechnung',
  /** Plattform-Einstellungen: Betreiber, App & Stripe, KI, Branding, Rechtstexte, E-Mail-Versand, Backup */
  'einstellungen',
  /** Nutzer & Rechte der Plattform verwalten */
  'nutzer',
] as const;
export type PlattformRecht = (typeof PLATTFORM_RECHTE)[number];

export const PLATTFORM_LEVEL = ['administrator', 'betrieb', 'abrechnung', 'support'] as const;
export type PlattformLevel = (typeof PLATTFORM_LEVEL)[number];
export const PlattformLevelSchema = z.enum(PLATTFORM_LEVEL);

const LEVEL_RECHTE: Record<PlattformLevel, readonly PlattformRecht[]> = {
  administrator: PLATTFORM_RECHTE,
  betrieb: ['marktplaetze_lesen', 'marktplaetze_bearbeiten', 'ansicht', 'abrechnung_lesen'],
  abrechnung: ['marktplaetze_lesen', 'abrechnung_lesen', 'abrechnung'],
  support: ['marktplaetze_lesen', 'ansicht'],
};

/** Bezeichnung und kurze Erklärung je Level – für Auswahl und Liste */
export const PLATTFORM_LEVEL_INFO: Record<PlattformLevel, { titel: string; text: string }> = {
  administrator: {
    titel: 'Administrator',
    text: 'Alles: Marktplätze, Pakete & Abrechnung, alle Plattform-Einstellungen sowie Nutzer & Rechte.',
  },
  betrieb: {
    titel: 'Betrieb',
    text: 'Marktplätze anlegen, bearbeiten und sperren, Inhaber einladen, Konto eines Marktplatzes öffnen. Abrechnung nur ansehen, keine Plattform-Einstellungen.',
  },
  abrechnung: {
    titel: 'Abrechnung',
    text: 'Pakete & Abrechnung: Pakete, Konditionen, Rechnungen erstellen, senden und Status setzen. Marktplätze nur ansehen.',
  },
  support: {
    titel: 'Support',
    text: 'Marktplätze ansehen und das Konto eines Marktplatzes öffnen – dort nur lesen, keine Änderungen.',
  },
};

export function plattformRechte(level: PlattformLevel | null | undefined): PlattformRecht[] {
  return level ? [...LEVEL_RECHTE[level]] : [];
}

export function plattformDarf(level: PlattformLevel | null | undefined, recht: PlattformRecht): boolean {
  return !!level && LEVEL_RECHTE[level].includes(recht);
}
